import React, { useState } from 'react';
import { MapPin, Clock, Mail, Instagram, ArrowRight, Car, Wifi, Music, Navigation, Copy, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { IMAGES } from '../mock/mock';

const ADDRESS_LINES = [
  'ST Fitness · inside Lycan Strength',
  'Unit 2, Oasis Business Centre',
  'Irlam, Manchester M44 5BL',
];
const ADDRESS_FLAT = 'ST Fitness, inside Lycan Strength, Unit 2, Oasis Business Centre, Irlam, Manchester M44 5BL';
const DEST_QUERY = 'Lycan+Strength+Unit+2+Oasis+Business+Centre+Irlam+M44+5BL';
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${DEST_QUERY}`;
const APPLE_MAPS_URL = `https://maps.apple.com/?daddr=${DEST_QUERY}&dirflg=d`;

const amenities = [
  { icon: Car, label: 'Free onsite parking' },
  { icon: Wifi, label: 'Free Wi-Fi' },
  { icon: Music, label: 'Full sound system' },
];

export default function LocationPage() {
  const [copied, setCopied] = useState(false);
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS_FLAT);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const isApple = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);

  return (
    <main data-testid="location-page">
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img src={IMAGES.groupLycan} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Find Us</div>
          <h1 className="mt-4 font-display italic text-white uppercase text-6xl md:text-8xl leading-[0.9]">ST <span className="text-brand">Fitness</span></h1>
          <p className="mt-5 max-w-2xl text-white/70">A fully equipped, purpose-built training space inside Lycan Strength, Irlam. Group classes, 1-2-1 sessions and community-first energy.</p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-directions-btn"
              className="inline-flex items-center gap-2 brand-gradient text-white uppercase tracking-widest text-xs px-7 py-4 glow-brand hover:opacity-95 transition-opacity"
            >
              <Navigation className="w-4 h-4" /> Get Directions <ArrowRight className="w-4 h-4" />
            </a>
            <button
              onClick={copyAddress}
              data-testid="hero-copy-address-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs px-7 py-4 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy Address'}
            </button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="reveal">
            <div className="aspect-[4/3] overflow-hidden border-4 border-brand relative group">
              <iframe
                title="ST Fitness Location"
                src="https://www.google.com/maps?q=Lycan+Strength+Unit+2+Oasis+Business+Centre+Irlam+M44+5BL&output=embed"
                className="w-full h-full"
                style={{ filter: 'grayscale(0.35) contrast(1.05)' }}
                loading="lazy"
              />
              {/* Floating quick-action ribbon over map */}
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="map-open-directions-btn"
                className="absolute top-4 right-4 inline-flex items-center gap-2 bg-black/85 backdrop-blur-sm border border-brand text-white text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:bg-brand transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open in Maps
              </a>
            </div>
          </div>

          <div className="reveal">
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Visit Us</div>
            <h2 className="font-display italic text-white text-5xl md:text-6xl uppercase mt-3 leading-[0.9]">Getting to <span className="text-brand">ST Fitness</span></h2>

            <div className="mt-6 space-y-4 text-white/80">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand mt-1 flex-shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">Address</div>
                  <address className="not-italic mt-1 leading-relaxed">
                    {ADDRESS_LINES.map((l) => <div key={l}>{l}</div>)}
                  </address>
                </div>
              </div>
              <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-brand mt-1" /><div><div className="text-[11px] uppercase tracking-[0.25em] text-white/50">Opening Hours</div><div>Monday to Friday</div></div></div>
              <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-brand mt-1" /><div><div className="text-[11px] uppercase tracking-[0.25em] text-white/50">Email</div><a href="mailto:sam.turner@st-fitness-coach.co.uk" className="hover:text-brand transition-colors">sam.turner@st-fitness-coach.co.uk</a></div></div>
              <div className="flex items-start gap-3"><Instagram className="w-5 h-5 text-brand mt-1" /><div><div className="text-[11px] uppercase tracking-[0.25em] text-white/50">Instagram</div><a href="https://instagram.com/samturner_fitness_and_family" target="_blank" rel="noreferrer" className="hover:text-brand transition-colors">@samturner_fitness_and_family</a></div></div>
            </div>

            {/* Directions block */}
            <div className="mt-8 p-5 bg-brand/10 border border-brand/40">
              <div className="flex items-center gap-2 text-brand font-bold text-[11px] uppercase tracking-[0.3em]">
                <Navigation className="w-4 h-4" /> One-tap directions
              </div>
              <p className="mt-2 text-white/75 text-sm">Get turn-by-turn from where you are, straight to the door.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="directions-google-btn"
                  className="inline-flex items-center gap-2 brand-gradient text-white uppercase tracking-widest text-xs px-5 py-3 glow-brand"
                >
                  <Navigation className="w-4 h-4" /> Google Maps <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={APPLE_MAPS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="directions-apple-btn"
                  className="inline-flex items-center gap-2 bg-white text-black hover:bg-brand hover:text-white transition-colors uppercase tracking-widest text-xs px-5 py-3"
                >
                  <Navigation className="w-4 h-4" /> Apple Maps
                </a>
                <button
                  onClick={copyAddress}
                  data-testid="directions-copy-btn"
                  className="inline-flex items-center gap-2 bg-transparent border border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs px-5 py-3 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {isApple ? (
                <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-white/45">iPhone user? Apple Maps opens native.</div>
              ) : (
                <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-white/45">Opens native Maps on mobile.</div>
              )}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {amenities.map((a, i) => (
                <Card key={i} className="bg-[#111116] border-white/5 rounded-none p-4 flex items-center gap-3">
                  <div className="w-9 h-9 grid place-items-center brand-gradient text-white"><a.icon className="w-4 h-4" /></div>
                  <span className="text-sm text-white/85">{a.label}</span>
                </Card>
              ))}
            </div>

            <Link to="/contact"><Button className="mt-8 brand-gradient text-white uppercase tracking-widest text-xs rounded-none px-7 py-6 glow-brand">Book a Session <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
